import { type ClassValue, clsx } from "clsx";
import { twMerge } from "tailwind-merge";

import type { TPart } from "~/database/types";

import { SERVER_URL } from "./constants";

export function cn(...inputs: ClassValue[]) {
    return twMerge(clsx(inputs));
}

/**
 * Returns the gradient styles used on meditation cards, reminder cards, and image placeholders
 * AIDEV-NOTE: Centralized gradient styling for consistent appearance across components
 */
export function getGradientCardStyles() {
    return [
        "bg-gradient-to-l from-rose-50/20 via-rose-50/10 via-75% to-transparent",
        "border border-t-rose-50 border-r-rose-100 border-b-rose-200 border-l-rose-100",
        "shadow-sm",
    ];
}

export function sanitizeFilename(filename: string): string {
    // AIDEV-NOTE: Enhanced filename sanitization for Supabase storage compatibility
    // Handle edge cases like brackets, parentheses, and other problematic characters
    return filename
        .normalize("NFD") // Normalize unicode characters
        .replace(/\p{Diacritic}/gu, "") // Remove diacritics using Unicode property
        .replace(/[\[\](){}<>]/g, "_") // Replace brackets and parentheses with underscore
        .replace(/[^a-zA-Z0-9.-_]/g, "_") // Replace any remaining unsafe chars with underscore
        .replace(/\s+/g, "_") // Replace spaces with underscore
        .replace(/_+/g, "_") // Collapse multiple underscores into single underscore
        .replace(/^_+|_+$/g, "") // Remove leading/trailing underscores
        .toLowerCase();
}

export function formatDuration(duration: number): string {
    const hours = Math.floor(duration / 3600);
    const minutes = Math.floor((duration % 3600) / 60);
    const seconds = Math.floor(duration % 60);
    return `${
        hours === 0 ? "" : hours.toString().padStart(2, "0") + ":"
    }${minutes.toString().padStart(2, "0")}:${seconds
        .toString()
        .padStart(2, "0")}`;
}

// AIDEV-NOTE: Returns a path-only URL. When exposing outside the app
// (copy/share/email), build an absolute URL with window.location.origin or SERVER_URL.
export function getSessionSharingUrl(sessionId: string, sessionToken: string) {
    const path = `/sessions/${sessionId}/sharing?sessionToken=${sessionToken}`;
    // AIDEV-NOTE: Sharing links should be absolute to work outside the app
    return `${SERVER_URL}${path}`;
}

export function getSessionUrl(sessionId: string, sessionToken?: string | null) {
    if (sessionToken) {
        return getSessionSharingUrl(sessionId, sessionToken);
    }

    return `/sessions/${sessionId}`;
}

export const getSessionPartUrl = (
    part: TPart,
    sessionId: string,
    sessionToken?: string | null,
) => {
    if (sessionToken) {
        const path = `/sessions/${sessionId}/sharing/parts/${part.id}?sessionToken=${sessionToken}`;
        // AIDEV-NOTE: When a sessionToken is present, treat as a share URL and include origin
        return `${SERVER_URL}${path}`;
    }
    // Internal navigation: return path-only
    return `/sessions/${sessionId}/parts/${part.id}`;
};

export const getSessionPartInteractionUrl = (
    partInteractionId: string,
    sessionId: string,
) => {
    return `/sessions/${sessionId}/parts/${partInteractionId}`;
};

export const getSessionTranscriptUrl = (
    sessionId: string,
    sessionToken?: string | null,
) => {
    if (sessionToken) {
        const path = `/sessions/${sessionId}/sharing/transcript?sessionToken=${sessionToken}`;
        // AIDEV-NOTE: When a sessionToken is present, treat as a share URL and include origin
        return `${SERVER_URL}${path}`;
    }
    // Internal navigation: return path-only
    return `/sessions/${sessionId}/transcript`;
};

/**
 * Capitalize a string (first letter uppercase, rest lowercase)
 */
export function capitalize(str: string): string {
    if (!str) return str;
    return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
}

/**
 * Capitalize first letter only, preserving the case of the rest
 */
export function capitalizeFirstLetter(str: string): string {
    if (!str) return str;
    return str.charAt(0).toUpperCase() + str.slice(1);
}

export function commaFormatList(items: string[]) {
    if (items.length === 0) return "";
    if (items.length === 1) return items[0];
    if (items.length === 2) return `${items[0]} and ${items[1]}`;

    const lastItem = items[items.length - 1];
    const initial = items.slice(0, -1).join(", ");
    return `${initial}, and ${lastItem}`;
}

interface DownloadFileOptions {
    /**
     * The API response containing the file data
     */
    response: Response;
    /**
     * Default filename to use if none is provided in Content-Disposition
     */
    defaultFilename: string;
    /**
     * MIME type of the file
     */
    mimeType?: string;
}

/**
 * Downloads a file from an API response
 */
export async function downloadFile({
    response,
    defaultFilename,
    mimeType = "text/markdown",
}: DownloadFileOptions) {
    if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || "Failed to download file");
    }

    // Get the filename from the Content-Disposition header
    const contentDisposition = response.headers.get("Content-Disposition");
    const filename =
        contentDisposition?.split("filename=")[1]?.replace(/"/g, "") ||
        defaultFilename;

    // Decide how to read the body based on MIME type
    let blob: Blob;
    if (mimeType.startsWith("text/")) {
        const textContent = await response.text();
        blob = new Blob([textContent], { type: mimeType });
    } else {
        // Binary – read as Blob directly to preserve bytes (e.g., DOCX, PDF)
        blob = await response.blob();
    }

    const url = window.URL.createObjectURL(blob);

    // Create a temporary link and click it to download
    const link = document.createElement("a");
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
}

export const base64ToBlob = (base64: string, mimeType: string): Blob => {
    try {
        // AIDEV-NOTE: Clean base64 string - remove data URI prefix if present
        let cleanBase64 = base64;
        if (base64.includes(",")) {
            cleanBase64 = base64.split(",")[1];
        }
        // Remove any whitespace
        cleanBase64 = cleanBase64.replace(/\s/g, "");

        // AIDEV-NOTE: Use chunked processing for large base64 strings to avoid memory issues
        const CHUNK_SIZE = 512 * 1024; // 512KB chunks
        const byteCharacters = atob(cleanBase64);
        const chunks: Uint8Array[] = [];

        for (
            let offset = 0;
            offset < byteCharacters.length;
            offset += CHUNK_SIZE
        ) {
            const chunk = byteCharacters.slice(offset, offset + CHUNK_SIZE);
            const byteNumbers = new Array(chunk.length);

            for (let i = 0; i < chunk.length; i++) {
                byteNumbers[i] = chunk.charCodeAt(i);
            }

            chunks.push(new Uint8Array(byteNumbers));
        }

        return new Blob(chunks, { type: mimeType });
    } catch (error) {
        console.error("Failed to convert base64 to blob:", error);
        throw new Error(
            `Failed to convert base64 to blob: ${
                error instanceof Error ? error.message : "Unknown error"
            }`,
        );
    }
};
